import { useEffect, useRef } from "react";

import type { GroupRegistryController } from "../groups/GroupRegistry.context";
import { reportGroupRegistryError } from "../groups/groupRegistry.repository";
import type { WorkspaceStoreApi } from "../workspace/Workspace.context";
import type {
  GroupColorSlot,
  GroupId,
  SymbolGroup,
} from "../workspace/workspace.types";
import { getEffectiveWidgetSymbol } from "../workspace/workspace.utils";
import { getGroupEligibleWidgetIds } from "../workspace/workspaceGroup";
import {
  type GroupSelectControllerReturn,
  useGroupSelectViewController,
} from "./GroupSelect.controller";
import { deriveGroupSelect } from "./GroupSelect.derive";

export const WORKSPACE_GROUP_NONE_LABEL = "No workspace group";

export interface UseWorkspaceGroupSelectControllerInput {
  groups: SymbolGroup[];
  workspaceGroupId: GroupId | null;
  memberCounts: Partial<Record<GroupId, number>>;
  linkedWidgetsByGroup: Partial<Record<GroupId, string>>;
  store: WorkspaceStoreApi;
  registry: GroupRegistryController | null;
}

export const useWorkspaceGroupSelectController = ({
  groups,
  workspaceGroupId,
  memberCounts,
  linkedWidgetsByGroup,
  store,
  registry,
}: UseWorkspaceGroupSelectControllerInput): GroupSelectControllerReturn => {
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const assignWorkspaceGroup = (groupId: GroupId | undefined) => {
    const state = store.getState();
    state.assignWidgetsGroup(getGroupEligibleWidgetIds(state.widgets), groupId);
  };

  const persistColor = (groupId: GroupId, colorSlot: GroupColorSlot) => {
    if (registry) {
      void registry
        .setGroupColor(groupId, colorSlot)
        .catch(reportGroupRegistryError);
      return;
    }
    store.getState().setGroupColor(groupId, colorSlot);
  };

  const persistName = (groupId: GroupId, name: string) => {
    if (registry) {
      void registry.setGroupName(groupId, name).catch(reportGroupRegistryError);
      return;
    }
    store.getState().setGroupName(groupId, name);
  };

  const persistDelete = (groupId: GroupId) => {
    if (registry) {
      void registry.deleteGroup(groupId).catch(reportGroupRegistryError);
      return;
    }
    store.getState().deleteGroup(groupId);
  };

  const persistCreate = () => {
    const state = store.getState();
    const eligibleIds = getGroupEligibleWidgetIds(state.widgets);
    const seedSymbol = eligibleIds
      .map((widgetId) => getEffectiveWidgetSymbol(state, widgetId))
      .find((symbol) => symbol !== undefined);

    if (!registry) {
      const created = state.createAndAssignGroup(eligibleIds[0] ?? "");
      if (created) {
        assignWorkspaceGroup(created);
      }
      return;
    }

    void registry
      .createGroup({ seedSymbol })
      .then((id) => {
        if (id === undefined || !mountedRef.current) {
          return;
        }
        assignWorkspaceGroup(id);
      })
      .catch(reportGroupRegistryError);
  };

  const derived = deriveGroupSelect({
    groups,
    activeGroupId: workspaceGroupId,
    memberCounts,
    linkedWidgetsByGroup,
    selectNone: () => {
      assignWorkspaceGroup(undefined);
    },
    selectGroup: (groupId) => {
      assignWorkspaceGroup(groupId);
    },
    createGroup: persistCreate,
    deleteGroup: persistDelete,
    setGroupName: persistName,
    setGroupColor: persistColor,
  });

  return useGroupSelectViewController({
    derived,
    noneLabel: WORKSPACE_GROUP_NONE_LABEL,
    unassignedAriaLabel: WORKSPACE_GROUP_NONE_LABEL,
  });
};
